export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WatermarkOptions {
  /** Watermark width as a fraction of the output width. */
  widthFraction: number;
  /** Edge margin in output pixels. */
  margin: number;
}

/**
 * Source crop to draw a `srcW×srcH` frame so it covers a `dstW×dstH` target without distortion
 * (CSS `object-fit: cover`), centred. Used to fit the camera frame to the capture canvas.
 */
export function coverFit(srcW: number, srcH: number, dstW: number, dstH: number): CropRect {
  const srcAspect = srcW / srcH;
  const dstAspect = dstW / dstH;

  if (srcAspect > dstAspect) {
    const sw = srcH * dstAspect; // too wide → crop the sides
    return { sx: (srcW - sw) / 2, sy: 0, sw, sh: srcH };
  }

  const sh = srcW / dstAspect; // too tall (or equal) → crop top/bottom
  return { sx: 0, sy: (srcH - sh) / 2, sw: srcW, sh };
}

export interface Size {
  width: number;
  height: number;
}

/** Default cap on a capture's long edge — bounds output/file size on high-DPI screens. */
export const DEFAULT_MAX_CAPTURE_EDGE = 1920;

/**
 * Output capture size: the source dimensions, preserving aspect, with the long edge clamped to
 * `maxLongEdge`. Never upscales — so a 480×640 sensor stays 480×640 rather than being tied to the
 * (possibly larger) screen drawing buffer, and a huge sensor is capped to a sane file size.
 */
export function captureSize(srcW: number, srcH: number, maxLongEdge: number): Size {
  const longEdge = Math.max(srcW, srcH);
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  return { width: Math.round(srcW * scale), height: Math.round(srcH * scale) };
}

/** DPR-rounding slop between two nominally equal aspects, not a genuinely different ratio. */
const ASPECT_TOLERANCE = 0.01;

/**
 * Whether two width/height pairs describe the same aspect ratio, within rounding slop.
 *
 * Used to guard a capture path where content is rendered once at a live viewport's aspect (a
 * homography-warped overlay, registered against that specific aspect) and a caller can separately
 * request a different output size — compositing that render into a mismatched-aspect canvas
 * stretches it out of registration with whatever it was tracking.
 *
 * The tolerance is applied to the *relative*, not absolute, difference between the two ratios —
 * an absolute comparison is orientation-dependent (the same tolerance reads as a far bigger
 * allowed stretch in portrait than in landscape, exactly backwards from where this product
 * actually runs), and would let through more visible misregistration than the tolerance's own
 * "rounding slop" rationale intends.
 *
 * Normalizing by the larger ratio is what makes "same aspect" an actual equivalence: the verdict
 * survives swapping the two pairs, and rotating both (which replaces each ratio with its
 * reciprocal). Dividing one ratio by the other is symmetric under neither, so the identical
 * physical mismatch could land on opposite sides of the tolerance purely by orientation.
 */
export function aspectsMatch(width: number, height: number, otherWidth: number, otherHeight: number): boolean {
  const ratio = width / height;
  const otherRatio = otherWidth / otherHeight;
  return Math.abs(ratio - otherRatio) / Math.max(ratio, otherRatio) <= ASPECT_TOLERANCE;
}

/** Bottom-right watermark rect, scaled to a fraction of output width with aspect preserved. */
export function watermarkRect(
  outW: number,
  outH: number,
  wmW: number,
  wmH: number,
  opts: WatermarkOptions,
): Rect {
  const w = outW * opts.widthFraction;
  const h = w * (wmH / wmW);
  return { x: outW - w - opts.margin, y: outH - h - opts.margin, w, h };
}
